import React from "react";
import {Alert} from "@mui/material";
import type {GridColDef} from "@mui/x-data-grid-premium";
import {
    PaymentsOutlined,
    ReceiptLongOutlined,
    SavingsOutlined,
    StorefrontOutlined,
} from "@mui/icons-material";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid";
import {Tag} from "~/components/tag/Tag";
import type {SitKpiPill} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import {usePtV2Context} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Context.ts";
import type {PtV2LeakageRow} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {leakageActionInput} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Calc.ts";
import {
    daysLost,
    daysLostLabel,
    formatEur,
    formatInt,
} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Format.ts";
import {usePtV2LeakageQuery} from "~/experiments/pages/payment-terms-v2/usePaymentTermsV2Queries.ts";
import {PtV2TabFrame} from "~/experiments/pages/payment-terms-v2/components/PtV2TabFrame.tsx";
import {PtV2SpendBar} from "~/experiments/pages/payment-terms-v2/components/PtV2SpendBar.tsx";
import {PtV2DetailDrawer} from "~/experiments/pages/payment-terms-v2/components/PtV2DetailDrawer.tsx";
import {PtV2LeakageDetail} from "~/experiments/pages/payment-terms-v2/components/PtV2LeakageDetail.tsx";
import {usePtV2Period} from "~/experiments/pages/payment-terms-v2/usePtV2Period.ts";

export const LeakageTab: React.FC = () => {
    const {summary, hasData} = usePtV2Context();
    const leakageQ = usePtV2LeakageQuery(hasData);
    const [selected, setSelected] = React.useState<PtV2LeakageRow | null>(null);
    const rows = React.useMemo(
        () => [...(leakageQ.data ?? [])].sort((a, b) => b.wcTrapped - a.wcTrapped),
        [leakageQ.data],
    );
    const k = summary?.kpis;
    const maxWc = Math.max(1, ...rows.map(r => r.wcTrapped));
    const period = usePtV2Period('leakage');

    const kpis: SitKpiPill[] = [
        {tone: 'ok', icon: <SavingsOutlined/>, label: 'Working capital trapped', value: formatEur(k?.leakageWc)},
        {tone: 'plain', icon: <StorefrontOutlined/>, label: 'Suppliers', value: formatInt(k?.leakageSuppliers)},
        {tone: 'info', icon: <ReceiptLongOutlined/>, label: 'Invoices affected', value: formatInt(k?.leakageInvoices)},
        {tone: 'plain', icon: <PaymentsOutlined/>, label: 'Spend in scope', value: formatEur(k?.leakageSpend)},
    ];

    const columns: GridColDef<PtV2LeakageRow>[] = [
        {field: 'supplier', headerName: 'Supplier', flex: 1.6, minWidth: 220,
            renderCell: p => <span className="ptv2-strong ptv2-ell" title={p.row.supplier}>{p.row.supplier}</span>},
        {field: 'contractTerm', headerName: 'Contract term', width: 130,
            renderCell: p => <Tag color="success" label={p.row.contractTerm}/>},
        {field: 'erpTerm', headerName: 'ERP term', width: 130,
            renderCell: p => <Tag color="warning" label={p.row.erpTerm}/>},
        {field: 'daysLost', headerName: 'Days lost', width: 110, type: 'number',
            valueGetter: (_v, row) => daysLost(row),
            renderCell: p => <span className="ptv2-warn ptv2-num">{daysLostLabel(p.row)} days</span>},
        {field: 'invoiceCount', headerName: 'Invoices', width: 100, type: 'number',
            renderCell: p => <span className="ptv2-num">{formatInt(p.row.invoiceCount)}</span>},
        {field: 'spend', headerName: 'Spend', width: 120, type: 'number',
            renderCell: p => <span className="ptv2-num">{formatEur(p.row.spend)}</span>},
        {field: 'wcTrapped', headerName: 'Working capital trapped', flex: 1, minWidth: 210, type: 'number',
            renderCell: p => <PtV2SpendBar ratio={p.row.wcTrapped / maxWc}>{formatEur(p.row.wcTrapped)}</PtV2SpendBar>},
    ];

    return <PtV2TabFrame
        tab="leakage"
        kpis={kpis}
        kpiAside={period}
        loading={leakageQ.isPending}
        error={leakageQ.isError}
        summary={<>Signed contracts grant longer terms than the ERP applies on the invoice. The gap pays suppliers early
            and ties up <b>{formatEur(k?.leakageWc)}</b> of working capital
            across {formatInt(k?.leakageSuppliers)} suppliers.</>}>
        <div className="ptv2-section">
            <Alert severity="info" icon={false}>
                <b>Working capital impact = annual spend × (contract days − applied ERP days) ÷ 365.</b>{' '}
                Select a supplier to see the calculation and the vendor master fix.
            </Alert>
            <div className="ptv2-grid-card">
                <CustomDataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={r => r.supplierKey}
                    onRowClick={p => setSelected(p.row as PtV2LeakageRow)}
                    autoHeight
                    hideFooter
                    disableRowSelectionOnClick
                    disableColumnMenu
                    className="ptv2-clickable-rows"
                    localeText={{noRowsLabel: 'No payment term leakage in this data'}}
                />
            </div>
        </div>
        <PtV2DetailDrawer
            open={!!selected}
            title={selected?.supplier ?? ''}
            subtitle="Payment term leakage"
            onClose={() => setSelected(null)}
            action={selected ? leakageActionInput(selected) : null}>
            {selected && <PtV2LeakageDetail row={selected}/>}
        </PtV2DetailDrawer>
    </PtV2TabFrame>;
};
